import { useMutation, useQueryClient } from '@common-fe/common-fe';
import _ from 'lodash';

import { api } from '@/api';
import { FALSE_KEY, TRUE_KEY } from '@/common/constants';
import { INVESTMENTS_FLOW_QUESTIONNAIRE, INVESTORS } from '@/common/paths';
import { getBooleanFromString } from '@/utils';

import {
  InvestmentPaths,
  ManagedInvestmentsQuestionnairePayload,
  SelfDirectedInvestmentsQuestionnairePayload,
} from '../investments.types';
import { useIsModelChangedStore } from '../stores';

import { INVESTOR_GET_QUERY_KEY } from './useInvestor.query';

interface Params {
  investmentType: InvestmentPaths;
  onSuccess?: () => void;
  onError?: () => void;
  setProposedToleranceModel?: (model: string) => void;
}

const useInvestmentsQuestionnaireQuery = ({
  onError,
  onSuccess,
  investmentType,
  setProposedToleranceModel,
}: Params) => {
  // const { handleSetValues } = useInvestmentsFieldsSetter({});
  const setModelChanged = useIsModelChangedStore((state) => state.setModelChanged);
  const queryClient = useQueryClient();
  const { mutateAsync, isLoading, isSuccess } = useMutation(
    (data: SelfDirectedInvestmentsQuestionnairePayload | ManagedInvestmentsQuestionnairePayload) => {
      let body: object = {};

      if (investmentType === InvestmentPaths.MANAGED) {
        const currentData = data as ManagedInvestmentsQuestionnairePayload;
        body = {
          investment_type: investmentType,
          employment_profile: {
            position: currentData.position,
            employment_type: currentData.employmentType,
            broker: currentData.broker ? getBooleanFromString(currentData.broker) : undefined,
            director_of:
              currentData.director === TRUE_KEY
                ? currentData.directorOf
                : currentData.director === FALSE_KEY
                  ? ''
                  : undefined,
            politically_exposed_names:
              currentData.politicallyExposed === TRUE_KEY
                ? currentData.politicallyExposedNames
                : currentData.politicallyExposed === FALSE_KEY
                  ? ''
                  : undefined,
          },
          investment_profile: {
            networth_total: _.toNumber(currentData.networthTotal),
            annual_income: _.toNumber(currentData.annualIncome),
            tolerance_model: currentData.toleranceModel,
            investment_objectives_type: currentData.investmentObjectivesType,
            investment_experience_type: currentData.investmentExperienceType,
            purpose_type: currentData.purposeType,
            risk_tolerance: currentData.riskToleranceType,
            medical_expenses_cover_type: currentData.medicalExpensesCoverType,
          },
        };
      } else if (investmentType === InvestmentPaths.SELF_DIRECTED) {
        const currentData = data as SelfDirectedInvestmentsQuestionnairePayload;
        body = {
          investment_type: investmentType,
          employment_profile: {
            position: currentData.position,
            employment_type: currentData.employmentType,
            broker: currentData.broker,
          },
          investment_profile: {
            networth_total: _.toNumber(currentData.networthTotal),
            annual_income: _.toNumber(currentData.annualIncome),

            investment_objectives_type: currentData.investmentObjectivesType,
            investment_experience_type: currentData.investmentExperienceType,
          },
        };
      }

      return api.patch(INVESTMENTS_FLOW_QUESTIONNAIRE, body);
    },
    {
      onSuccess: (data) => {
        setModelChanged(false);
        if (setProposedToleranceModel) {
          setProposedToleranceModel(data.data?.investor_profile?.proposed_tolerance_model);
        }
        queryClient.invalidateQueries({
          queryKey: [INVESTOR_GET_QUERY_KEY, INVESTORS],
        });
        queryClient.invalidateQueries({
          queryKey: [INVESTORS],
        });
        if (onSuccess) {
          onSuccess();
        }
      },
      onError: () => {
        if (onError) onError();
      },
    }
  );
  return {
    handleSave: mutateAsync,
    isLoading,
    isSuccess,
  };
};

export default useInvestmentsQuestionnaireQuery;
